import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getABTest } from 'utils/taplytics';
import { safeParsePrice } from 'utils/currency';
import { Button } from '@reservamos/elements';
import MainButton from '../ui/atoms/MainButton';
import {
  GontijoCheckoutLoginButton,
  useGontijoCheckoutLoginNeeded,
} from './gontijo/GontijoCheckoutLoginButton';
import LocalizedCurrency from './LocalizedCurrency';
import quickPurchaseIconSrc from '../images/purchases/quick-purchase.png';

const propTypes = {
  paymentType: PropTypes.string.isRequired,
  paymentEngine: PropTypes.string.optional,
  totalPrice: PropTypes.string.isRequired,
  isExchange: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isPointsPaying: PropTypes.bool,
  walletText: PropTypes.string,
  isDisabled: PropTypes.bool,
  variant: PropTypes.string,
};

/**
 * This component is used to display a payment button.
 * @typedef {Object} PaymentButtonProps
 * @param {Object} props
 * @param {string} props.paymentType - The type of payment.
 * @param {string} props.paymentEngine - The engine of payment.
 * @param {string} props.totalPrice - The total price of the payment.
 * @param {boolean} props.isExchange - Whether the payment is an exchange.
 * @param {function} props.onClick - The function to be called when the button is clicked.
 * @param {boolean} props.isLoading - Whether the button is loading.
 * @param {boolean} props.isPointsPaying - Whether the payment is using points.
 * @param {string} props.walletText - The text to be displayed when the payment is using points.
 * @param {boolean} props.isDisabled - Whether the button is disabled.
 * @param {string} props.variant - Optional button variant (e.g. 'quickPurchase').
 */
const PaymentButton = ({
  paymentType,
  paymentEngine,
  totalPrice,
  isExchange,
  onClick,
  isLoading,
  isPointsPaying,
  walletText,
  isDisabled = false,
  variant,
}) => {
  const { t } = useTranslation();
  // Gontijo sells to signed-in buyers only, so for them this button is the sign-in until there is
  // a session. Decided here rather than in each checkout because both surfaces -- `PaymentPanel`
  // and the older `Checkout` -- render their CTA through this one component, and a CTA that says
  // "pay" in one of them and "sign in" in the other is the bug this avoids. Every other brand
  // reads false and renders exactly what it rendered before.
  const needsGontijoLogin = useGontijoCheckoutLoginNeeded({ isExchange });
  const paymentsToIgnoreCurrency = ['free_pay'];
  const ABVariable = getABTest('payment', 'A');

  let paymentTypeAB = paymentType;
  if (paymentEngine && ['aplazo'].includes(paymentEngine)) {
    paymentTypeAB += `_${paymentEngine}`;
  }
  if (ABVariable !== 'A') {
    paymentTypeAB += `_${ABVariable}`;
  }

  const text = isExchange
    ? t('payment:button.confirm_exchange')
    : `${t('payment:button.payment', { context: paymentTypeAB })} `;

  let totalTextComponent = (
    <>
      {text}
      {!paymentsToIgnoreCurrency.includes(paymentType) && totalPrice && (
        <>
          {' '}
          <LocalizedCurrency
            price={safeParsePrice(totalPrice)}
            currencyStyle="currency"
            decimals={2}
            color="white"
            weight="semibold"
            size="L"
          />
        </>
      )}
    </>
  );

  if (!totalPrice && !isExchange) {
    totalTextComponent = t('payment:button.confirm_payment');
  }
  if (isPointsPaying) totalTextComponent = walletText;

  // Before the branches below: for a buyer who has to sign in there is no total to price and no
  // wallet text to honour, and the quickPurchase treatment would only dress up a button that does
  // not pay.
  if (needsGontijoLogin) {
    return <GontijoCheckoutLoginButton isLoading={isLoading} />;
  }

  if (variant === 'quickPurchase') {
    // The design-system Button dims on isDisabled but keeps cursor:default and
    // still applies its :hover background (its :hover rule is declared after
    // :disabled with equal specificity). Since Button forwards no className, we
    // wrap it: the container carries the not-allowed cursor and pointer-events
    // are removed from the button so its hover never triggers. Gated on
    // !isLoading so the loading (spinner) state keeps its own look.
    return (
      <div
        className={
          isDisabled && !isLoading ? 'cursor-not-allowed [&>button]:pointer-events-none' : undefined
        }
      >
        <Button
          variant="quickPurchase"
          onClick={onClick}
          isLoading={isLoading}
          isDisabled={isDisabled}
          isRounded
          fontSize="L"
          fontWeight="semibold"
        >
          <div className="grid grid-cols-[auto_auto] gap-2 py-2 pl-[20px] sm:pl-0 sm:py-0">
            <img src={quickPurchaseIconSrc} alt="" width={16} height="auto" className="mr-1" />
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {totalTextComponent}
            </div>
          </div>
        </Button>
      </div>
    );
  }

  return (
    <MainButton
      onClick={onClick}
      label={totalTextComponent}
      isLoading={isLoading}
      isDisabled={isDisabled}
    />
  );
};

PaymentButton.propTypes = propTypes;

export default PaymentButton;
